import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createSelfHostedCameraApi } from '../../services/selfHostedCameraApi';
import CameraAudienceChooser from './CameraAudienceChooser';
import './Camera.css';

const same = (a, b) => a.audio_enabled === b.audio_enabled
  && a.default_audiences.length === b.default_audiences.length
  && a.default_audiences.every((v, i) => v === b.default_audiences[i]);

// Owner-scoped /api/camera/settings. The API adapter validates every array slot
// before normalizing, so malformed authority renders as unavailable instead of
// silently defaulting to Private. Saves are confirmed by an independent readback.
export default function CameraSettingsSection({ backendService, identity, ownerId, createApi = createSelfHostedCameraApi }) {
  const api = useMemo(() => {
    try { return createApi({ backendService, identity, ownerId }); } catch { return null; }
  }, [backendService, identity, ownerId, createApi]);
  const generation = useRef({ api, id: 0 });
  if (generation.current.api !== api) generation.current = { api, id: generation.current.id + 1 };
  return <ScopedCameraSettings key={generation.current.id} api={api} />;
}

function ScopedCameraSettings({ api }) {
  const alive = useRef(true);
  useLayoutEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const [reload, setReload] = useState(0);
  const [status, setStatus] = useState('loading');
  const [value, setValue] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let alive = true;
    setStatus('loading'); setValue(null); setMessage(null);
    if (!api) { setStatus('unavailable'); return undefined; }
    api.getSettings().then(settings => {
      if (!alive) return;
      setValue(settings); setStatus('ready');
    }).catch(() => { if (alive) setStatus('unavailable'); });
    return () => { alive = false; };
  }, [api, reload]);

  const save = async () => {
    if (!alive.current || !api || !value || status !== 'ready') return;
    const requested = value;
    setStatus('saving'); setMessage(null);
    try {
      const written = await api.putSettings(requested);
      if (!alive.current) return;
      const readback = await api.getSettings();
      if (!alive.current) return;
      if (!same(written, requested) || !same(readback, requested)) {
        setValue(readback);
        setMessage({ type: 'error', text: 'Camera settings could not be confirmed. Showing what Kata.fit saved.' });
      } else {
        setValue(readback);
        setMessage({ type: 'ok', text: 'Camera settings saved.' });
      }
      setStatus('ready');
    } catch (error) {
      if (!alive.current) return;
      const invalid = error?.code === 'CAMERA_INVALID_RESPONSE' || error?.code === 'REQUEST_ACCOUNT_CHANGED';
      if (invalid) setValue(null);
      setStatus(invalid ? 'unavailable' : 'ready');
      setMessage({ type: 'error', text: 'Camera settings could not be confirmed. The save may have reached Kata.fit. Reload settings to check before trying again.' });
    }
  };

  const disabled = status !== 'ready';
  return (
    <div className="camera-settings" data-testid="camera-settings">
      <div className="location-tracking-info">
        <span className="location-tracking-label">📹 Camera broadcasts</span>
        <span className="location-tracking-desc">
          The camera turns on only when you tap Preview and then Start from the camera button at the top of the screen. Nothing is captured automatically, including during workouts. People in the audience you choose can watch live, and the broadcast is recorded for that same audience.
        </span>
      </div>
      {status === 'unavailable' && (
        <p className="camera-panel-error" role="alert">Camera settings are unavailable right now. Reload to check the current settings.</p>
      )}
      <CameraAudienceChooser legend="Default audience" value={status === 'unavailable' ? null : value?.default_audiences ?? null}
        disabled={disabled} onChange={next => setValue(prev => ({ ...prev, default_audiences: next }))} />
      <p className="camera-panel-note">Private: only you. Public: any signed-in Kata.fit members, never anonymous visitors. Friends, Dojo and Dojo Chief can be combined.</p>
      <label className="camera-mic-toggle">
        <input type="checkbox" aria-label="Include microphone audio by default" disabled={disabled}
          checked={value?.audio_enabled === true}
          onChange={e => { const audio = e.target.checked; setValue(prev => ({ ...prev, audio_enabled: audio })); }} />
        <span>Include microphone audio by default</span>
        <span className="camera-panel-note">Off by default. Each Preview still starts with the microphone off until you turn it on.</span>
      </label>
      {message && <p className={message.type === 'ok' ? 'camera-panel-note' : 'camera-panel-error'} role="status">{message.text}</p>}
      {(status === 'unavailable' || message?.type === 'error') && <button type="button" className="camera-secondary-btn" disabled={status === 'loading' || status === 'saving'} onClick={() => setReload(n => n + 1)}>Reload camera settings</button>}
      <button type="button" className="camera-secondary-btn" disabled={disabled} onClick={() => { void save(); }}>Save camera settings</button>
    </div>
  );
}
